import { toPlainText } from './richText';
import { monthKey } from './format';
import type { FinderDate, FinderEvent } from './types';

export type SignupType = 0 | 1 | 2;

/** What the visitor can change (and what the URL carries). */
export interface FilterState {
  campus: number | null;
  ministry: number | null;
  program: number | null;
  eventType: number | null;
  /** "YYYY-MM" */
  month: string | null;
  /** 0 any, 1 registration open, 2 volunteer opportunities */
  signup: SignupType;
  featured: boolean;
  search: string;
}

/** What the page embeds bake in. Never shown as chips, never written to the URL. */
export interface EmbedOptions {
  includeTypes: number[];
  excludeTypes: number[];
  includeMinistries: number[];
  excludeMinistries: number[];
  includePrograms: number[];
  excludePrograms: number[];
  excludeEvents: number[];
  /** Pin the list to specific events / series members (deep links) */
  eventIds: number[];
  /** Stock "reduceseriesto": show at most N upcoming dates per series */
  maxPerSeries: number | null;
  /** Cap the number of cards */
  limit: number | null;
  /** How far ahead the API should look (days); null = the API default */
  days: number | null;
  hideFilters: boolean;
  hideSearch: boolean;
  hideCampus: boolean;
}

export const EMPTY_FILTERS: FilterState = {
  campus: null,
  ministry: null,
  program: null,
  eventType: null,
  month: null,
  signup: 0,
  featured: false,
  search: '',
};

export const EMPTY_EMBED: EmbedOptions = {
  includeTypes: [],
  excludeTypes: [],
  includeMinistries: [],
  excludeMinistries: [],
  includePrograms: [],
  excludePrograms: [],
  excludeEvents: [],
  eventIds: [],
  maxPerSeries: null,
  limit: null,
  days: null,
  hideFilters: false,
  hideSearch: false,
  hideCampus: false,
};

/** Number of filters the visitor has set, for the badge on the Filters button. */
export function countActive(f: FilterState): number {
  let n = 0;
  if (f.ministry !== null) n++;
  if (f.program !== null) n++;
  if (f.eventType !== null) n++;
  if (f.month !== null) n++;
  if (f.signup !== 0) n++;
  if (f.featured) n++;
  if (f.search.trim()) n++;
  return n;
}

// ---------------------------------------------------------------------------
// Params <-> state
//
// Short keys are what the widget writes to the URL. The stock
// <mpp-event-finder> attribute names (congregationid, ministryid, monthid,
// signuptype, isfeatured, reduceseriesto...) are read too, so an embed can
// be swapped attribute for attribute.
// ---------------------------------------------------------------------------

const FILTER_KEYS: Record<string, keyof FilterState> = {
  campus: 'campus',
  congregationid: 'campus',
  congregation: 'campus',
  ministry: 'ministry',
  ministryid: 'ministry',
  program: 'program',
  programid: 'program',
  type: 'eventType',
  eventtype: 'eventType',
  eventtypeid: 'eventType',
  month: 'month',
  monthid: 'month',
  signup: 'signup',
  signuptype: 'signup',
  featured: 'featured',
  isfeatured: 'featured',
  q: 'search',
  search: 'search',
  keyword: 'search',
};

const EMBED_KEYS: Record<string, keyof EmbedOptions> = {
  types: 'includeTypes',
  eventtypes: 'includeTypes',
  includetypes: 'includeTypes',
  includeeventtypes: 'includeTypes',
  excludetypes: 'excludeTypes',
  excludeeventtypes: 'excludeTypes',
  ministries: 'includeMinistries',
  includeministries: 'includeMinistries',
  excludeministries: 'excludeMinistries',
  programs: 'includePrograms',
  includeprograms: 'includePrograms',
  excludeprograms: 'excludePrograms',
  exclude: 'excludeEvents',
  excludeevents: 'excludeEvents',
  excludeeventids: 'excludeEvents',
  events: 'eventIds',
  eventids: 'eventIds',
  series: 'maxPerSeries',
  reduceseriesto: 'maxPerSeries',
  maxperseries: 'maxPerSeries',
  limit: 'limit',
  max: 'limit',
  days: 'days',
  daysahead: 'days',
  hidefilters: 'hideFilters',
  hidesearch: 'hideSearch',
  hidecampus: 'hideCampus',
};

function norm(key: string): string {
  // "hide-filters", "hideFilters", "@MinistryID" -> "hidefilters", "ministryid"
  return key.trim().replace(/^@/, '').replace(/[-_]/g, '').toLowerCase();
}

function toInt(value: string): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? Math.trunc(n) : null;
}

function toIntList(value: string): number[] {
  return value
    .split(',')
    .map((s) => toInt(s.trim()))
    .filter((n): n is number => n !== null);
}

function toBool(value: string): boolean {
  const v = value.trim().toLowerCase();
  return v === '1' || v === 'true' || v === 'yes' || v === 'on';
}

/**
 * "9" (stock monthid) -> the next September from today; "2026-09" as is.
 */
export function resolveMonth(value: string, now: Date = new Date()): string | null {
  const v = value.trim();
  if (/^\d{4}-\d{2}$/.test(v)) return v;
  const m = toInt(v);
  if (m === null || m < 1 || m > 12) return null;
  const year = m >= now.getMonth() + 1 ? now.getFullYear() : now.getFullYear() + 1;
  return `${year}-${String(m).padStart(2, '0')}`;
}

export function resolveSignup(value: string): SignupType {
  const v = value.trim().toLowerCase();
  if (v === '1' || v === 'registration' || v === 'register' || v === 'registrations') return 1;
  if (v === '2' || v === 'volunteer' || v === 'serve' || v === 'opportunities') return 2;
  return 0;
}

/**
 * Apply raw key/value pairs (page URL, or the embed's data-params /
 * data-* attributes) on top of a filter state. Unknown keys are ignored.
 * Campus accepts a Congregation_ID, MP's Campus_Slug ("troy") or a name.
 */
export function applyRawParams(
  base: FilterState,
  entries: Iterable<[string, string]>,
  resolveCampus: (value: string) => number | null
): FilterState {
  const next: FilterState = { ...base };
  for (const [rawKey, rawValue] of entries) {
    const key = FILTER_KEYS[norm(rawKey)];
    const value = rawValue.trim();
    if (!key || !value) continue;
    switch (key) {
      case 'campus':
        next.campus = resolveCampus(value);
        break;
      case 'ministry':
        next.ministry = toInt(value);
        break;
      case 'program':
        next.program = toInt(value);
        break;
      case 'eventType':
        next.eventType = toInt(value);
        break;
      case 'month':
        next.month = resolveMonth(value);
        break;
      case 'signup':
        next.signup = resolveSignup(value);
        break;
      case 'featured':
        next.featured = toBool(value);
        break;
      case 'search':
        next.search = value;
        break;
    }
  }
  return next;
}

/** The embed-level constraints from the same key/value pairs. */
export function parseEmbedOptions(entries: Iterable<[string, string]>): EmbedOptions {
  const out: EmbedOptions = { ...EMPTY_EMBED };
  for (const [rawKey, rawValue] of entries) {
    const key = EMBED_KEYS[norm(rawKey)];
    const value = rawValue.trim();
    if (!key || !value) continue;
    switch (key) {
      case 'includeTypes':
      case 'excludeTypes':
      case 'includeMinistries':
      case 'excludeMinistries':
      case 'includePrograms':
      case 'excludePrograms':
      case 'excludeEvents':
      case 'eventIds':
        out[key] = toIntList(value);
        break;
      case 'maxPerSeries':
      case 'limit':
      case 'days': {
        const n = toInt(value);
        out[key] = n !== null && n > 0 ? n : null;
        break;
      }
      case 'hideFilters':
      case 'hideSearch':
      case 'hideCampus':
        out[key] = toBool(value);
        break;
    }
  }
  return out;
}

export type SlugFor = (id: number) => string | null;

/**
 * Serialize the visitor's filters into short query params. The campus is
 * written as its slug ("troy") when one is known so links read well.
 */
export function filtersToParams(f: FilterState, slugFor?: SlugFor): URLSearchParams {
  const p = new URLSearchParams();
  if (f.campus !== null) p.set('campus', slugFor?.(f.campus) ?? String(f.campus));
  if (f.ministry !== null) p.set('ministry', String(f.ministry));
  if (f.program !== null) p.set('program', String(f.program));
  if (f.eventType !== null) p.set('type', String(f.eventType));
  if (f.month !== null) p.set('month', f.month);
  if (f.signup !== 0) p.set('signup', f.signup === 1 ? 'registration' : 'volunteer');
  if (f.featured) p.set('featured', '1');
  if (f.search.trim()) p.set('q', f.search.trim());
  return p;
}

const MANAGED_KEYS = new Set(Object.keys(FILTER_KEYS));

/**
 * Rewrite the page URL so the current filters are shareable, leaving every
 * query param the widget doesn't own (WordPress previews, UTM tags) intact.
 */
export function syncFiltersToUrl(f: FilterState, slugFor?: SlugFor): void {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    for (const key of Array.from(url.searchParams.keys())) {
      if (MANAGED_KEYS.has(norm(key))) url.searchParams.delete(key);
    }
    for (const [k, v] of filtersToParams(f, slugFor)) url.searchParams.set(k, v);
    const next = url.toString();
    if (next !== window.location.href) window.history.replaceState(window.history.state, '', next);
  } catch {
    /* URL APIs unavailable, nothing to sync */
  }
}

// ---------------------------------------------------------------------------
// Matching
// ---------------------------------------------------------------------------

/** Church Wide events belong to every campus, same as the stock widget. */
const CHURCH_WIDE = 1;

function normalize(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

export function haystack(e: FinderEvent): string {
  return normalize(
    [
      e.title,
      e.shortDescription ?? '',
      toPlainText(e.description ?? ''),
      e.congregationName,
      e.locationName ?? '',
      e.city ?? '',
      e.eventType ?? '',
      e.programName ?? '',
      e.ministryName ?? '',
    ].join(' ')
  );
}

export interface FilterHit {
  event: FinderEvent;
  /** The dates that match (month, exclusions), soonest first, trimmed to maxPerSeries */
  dates: FinderDate[];
}

function passesEmbed(e: FinderEvent, o: EmbedOptions): boolean {
  if (
    o.eventIds.length > 0 &&
    !o.eventIds.includes(e.id) &&
    !e.dates.some((d) => o.eventIds.includes(d.id))
  )
    return false;
  if (
    o.includeTypes.length > 0 &&
    (e.eventTypeId === null || !o.includeTypes.includes(e.eventTypeId))
  )
    return false;
  if (e.eventTypeId !== null && o.excludeTypes.includes(e.eventTypeId)) return false;
  if (
    o.includeMinistries.length > 0 &&
    (e.ministryId === null || !o.includeMinistries.includes(e.ministryId))
  )
    return false;
  if (e.ministryId !== null && o.excludeMinistries.includes(e.ministryId)) return false;
  if (
    o.includePrograms.length > 0 &&
    (e.programId === null || !o.includePrograms.includes(e.programId))
  )
    return false;
  if (e.programId !== null && o.excludePrograms.includes(e.programId)) return false;
  return true;
}

export function matchEvent(
  e: FinderEvent,
  f: FilterState,
  o: EmbedOptions,
  hay: string = haystack(e)
): FilterHit | null {
  if (!passesEmbed(e, o)) return null;
  if (f.campus !== null && e.congregationId !== f.campus && e.congregationId !== CHURCH_WIDE)
    return null;
  if (f.ministry !== null && e.ministryId !== f.ministry) return null;
  if (f.program !== null && e.programId !== f.program) return null;
  if (f.eventType !== null && e.eventTypeId !== f.eventType) return null;
  if (f.signup === 2 && e.opportunityCount <= 0) return null;

  let dates = e.dates;
  if (o.excludeEvents.length > 0) dates = dates.filter((d) => !o.excludeEvents.includes(d.id));
  if (o.eventIds.length > 0 && dates.some((d) => o.eventIds.includes(d.id)))
    dates = dates.filter((d) => o.eventIds.includes(d.id));
  if (f.month !== null) dates = dates.filter((d) => monthKey(d.start) === f.month);
  if (f.featured && !dates.some((d) => d.featured)) return null;
  if (f.signup === 1 && !dates.some((d) => d.registrationOpen)) return null;
  if (dates.length === 0) return null;

  const terms = normalize(f.search).split(/\s+/).filter(Boolean);
  if (terms.length > 0 && !terms.every((t) => hay.includes(t))) return null;

  if (o.maxPerSeries !== null && dates.length > o.maxPerSeries)
    dates = dates.slice(0, o.maxPerSeries);
  return { event: e, dates };
}

/** Everything that matches, soonest visible date first, capped by the embed's limit. */
export function filterEvents(events: FinderEvent[], f: FilterState, o: EmbedOptions): FilterHit[] {
  const hits: FilterHit[] = [];
  for (const e of events) {
    const hit = matchEvent(e, f, o);
    if (hit) hits.push(hit);
  }
  hits.sort((a, b) => a.dates[0].start.localeCompare(b.dates[0].start) || a.event.id - b.event.id);
  return o.limit !== null ? hits.slice(0, o.limit) : hits;
}

/** The months that have at least one date, in order, for the month filter. */
export function availableMonths(events: FinderEvent[]): string[] {
  const set = new Set<string>();
  for (const e of events) for (const d of e.dates) set.add(monthKey(d.start));
  return Array.from(set).sort();
}
